import type { MasterItemDto } from '@talentbabu/types';
import type { MasterType } from '@talentbabu/validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';

export interface LocationItem extends MasterItemDto {
  state: string;
  district?: string;
  stateCode?: string;
  country?: string;
}

export function useMasters<T extends MasterItemDto = MasterItemDto>(type: MasterType) {
  return useQuery({
    queryKey: ['masters', type],
    queryFn: async () => (await apiGet<T[]>(`/masters/${type}`)).data,
    staleTime: 30 * 60_000,
  });
}

export const useLocations = () => useMasters<LocationItem>('locations');
export const useIndustries = () => useMasters('industries');

function useIndustryMutation<T, R>(fn: (input: T) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['masters', 'industries'] }),
  });
}

/** Adds an industry, or renames one when `id` is given. */
export const useSaveIndustry = () =>
  useIndustryMutation(async ({ id, name }: { id?: string; name: string }) =>
    id
      ? (await apiPatch<MasterItemDto>(`/masters/industries/${id}`, { name })).data
      : (await apiPost<MasterItemDto>('/masters/industries', { name })).data,
  );

export const useDeleteIndustry = () =>
  useIndustryMutation(async (id: string) => apiDelete(`/masters/industries/${id}`));
